import { orderBy } from 'lodash/fp';
import styled from 'styled-components';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { Role } from 'dashboard/models/role';
import { User } from 'dashboard/models/user';
import RoleUsersListRow from 'dashboard/views/DashboardUserRolesShowView/RoleUsersListRow';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Table, { TableBody, TableHeaderCell, TableRow } from 'design-system/components/Table';
import { Heading, Paragraph } from 'design-system/components/Text';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';

interface RoleUsersListProps {
  canAssignRoleUsers: boolean;
  currentUser: User;
  loadingRoleUsers: boolean;
  roles: Role[];
  roleUsers: User[];
  userSearchTerm: string;
  openAddUserModal: () => void;
  setUserSearchTerm: (term: string) => void;
  setUserToRemove: (user: User) => void;
}

const RoleUsersList = ({
  canAssignRoleUsers,
  currentUser,
  loadingRoleUsers,
  roles,
  roleUsers,
  userSearchTerm,
  openAddUserModal,
  setUserSearchTerm,
  setUserToRemove,
}: RoleUsersListProps) => {
  const hasUserAssignPermission = usePermission(Permission.RoleAssignUser);

  const userRows = orderBy(
    ['attributes.first-name', 'attributes.last-name', 'attributes.email-address'],
    ['desc', 'asc', 'asc', 'asc'],
    roleUsers,
  );

  return (
    <>
      <Card>
        <Card.Body>
          <StyledTitleContainer>
            <Card.Title>
              <Heading size='sm'>Role Users</Heading>
            </Card.Title>
            {hasUserAssignPermission && canAssignRoleUsers && (
              <Button
                id='role-users-list__add-user'
                data-test='role-users-list__add-user'
                aria-label='Add user'
                onClick={openAddUserModal}
                variant='basic'
                width='auto'
              >
                Add user
              </Button>
            )}
          </StyledTitleContainer>
          <SearchInput
            placeholder='Search by name or email'
            title={null}
            value={userSearchTerm}
            handleInput={setUserSearchTerm}
            size='md'
          />
          <Spacer height={1} />
          {loadingRoleUsers ? (
            <LoadingIndicator />
          ) : (
            <Table data-test='role-users-list__table'>
              <TableRow>
                <TableHeaderCell>
                  <Paragraph color='primary'>Name</Paragraph>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Paragraph color='primary'>Email</Paragraph>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Paragraph color='primary'>Roles</Paragraph>
                </TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
              <TableBody>
                {userRows.map((user) => (
                  <RoleUsersListRow
                    key={user.id}
                    currentUser={currentUser}
                    disableRemoveUser={roleUsers.length === 1}
                    roles={roles}
                    user={user}
                    setUserToRemove={setUserToRemove}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default RoleUsersList;

const StyledTitleContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;
